import { InitialStopFormValues } from '@common/stops/types';
import { timeStringToTimeInteger } from '@utils/dateFormat';

type ConfirmedAppointmentFormValues = Pick<
  InitialStopFormValues,
  | 'appointmentSchedulingStatus'
  | 'beginTimeRange'
  | 'dateAppointment'
  | 'stopDateTime'
  | 'timeAppointment'
  | 'timezone'
  | 'appointmentTimeZone'
>;

type AppointmentSchedulingPayload = Pick<
  InitialStopFormValues,
  | 'appointmentSchedulingStatus'
  | 'aiSchedulingEnabled'
  | 'appointmentPreference'
>;

type StopAppointmentFormValues = AppointmentSchedulingPayload &
  ConfirmedAppointmentFormValues;

export const appointmentSchedulingPayload = (
  values: AppointmentSchedulingPayload,
  enabled: boolean,
): Partial<AppointmentSchedulingPayload> =>
  enabled
    ? {
        appointmentSchedulingStatus: values.appointmentSchedulingStatus,
        aiSchedulingEnabled: values.aiSchedulingEnabled,
        appointmentPreference: values.appointmentPreference,
      }
    : {};

export const appointmentSchedulingStopFields = (
  values: StopAppointmentFormValues,
  enabled: boolean,
) => ({
  ...appointmentSchedulingPayload(values, enabled),
  ...confirmedAppointmentStopFields({
    ...values,
    appointmentSchedulingStatus: enabled
      ? values.appointmentSchedulingStatus
      : '',
  }),
});
type CopiedAppointmentFields = {
  appointmentSchedulingStatus?: string | null;
  aiSchedulingEnabled?: boolean;
  appointmentPreference?: string | null;
  appointmentNum?: string | null;
  appointmentDate?: string | null;
  appointment?: { date: string; time: string };
  needsAppointment?: boolean;
  beginTimeRange?: number | null;
  endTimeRange?: number | null;
};

export const copiedAppointmentFields = (stop: CopiedAppointmentFields) => {
  if (
    !stop.appointmentSchedulingStatus &&
    stop.aiSchedulingEnabled !== true &&
    !stop.appointmentPreference
  )
    return {};

  const hadBooking =
    stop.appointmentSchedulingStatus === 'AppointmentRequested' ||
    stop.appointmentSchedulingStatus === 'AppointmentConfirmed';
  const status = hadBooking
    ? 'Required'
    : (stop.appointmentSchedulingStatus ?? '');

  return {
    appointmentSchedulingStatus: status,
    aiSchedulingEnabled: false,
    appointmentPreference: stop.appointmentPreference ?? '',
    needsAppointment: Boolean(status),
    appointmentDate: undefined,
    appointmentNum: hadBooking ? undefined : stop.appointmentNum,
    appointment: hadBooking ? undefined : stop.appointment,
    beginTimeRange: hadBooking ? null : stop.beginTimeRange,
    endTimeRange: hadBooking ? null : stop.endTimeRange,
  };
};

export const confirmedAppointmentStopFields = (
  values: ConfirmedAppointmentFormValues,
) => {
  if (values.appointmentSchedulingStatus !== 'AppointmentConfirmed') return {};

  const timePart = values.timeAppointment.includes('T')
    ? values.timeAppointment.split('T')[1].slice(0, 5)
    : values.timeAppointment.slice(0, 5);
  const appointmentBegin = timePart ? timeStringToTimeInteger(timePart) : null;
  const appointmentDate = values.dateAppointment || values.stopDateTime;

  return {
    stopDateTime: appointmentDate || values.stopDateTime,
    beginTimeRange: appointmentBegin ?? values.beginTimeRange,
    timezone: values.appointmentTimeZone || values.timezone,
  };
};
